import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Button,
  Tooltip,
  IconButton,
  Collapse,
  LinearProgress,
  Stack,
  Avatar,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
} from '@mui/material';
import {
  School as SchoolIcon,
  LocationOn as LocationIcon,
  Groups as GroupsIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon,
  PlayArrow as PlayArrowIcon,
  GetApp as GetAppIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  DeleteForever as HardDeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { adminService, clearAdminCache } from '../../services/adminService';
import { 
  detectSchoolType, 
  getSchoolTypeInfo, 
  canDeleteSchoolType, 
  getDeletionWarning,
  SCHOOL_TYPE_LABELS 
} from '../../utils/schoolTypeDetection';

const DATA_POINT_CONFIGS = {
  display_name: {
    label: 'Display Name',
    icon: <SchoolIcon />,
    description: 'School display name for public presentation'
  },
  coordinates: {
    label: 'Coordinates',
    icon: <LocationIcon />,
    description: 'Latitude and longitude for mapping and spatial analysis'
  },
  esri_demographic_data: {
    label: 'ESRI Demographics',
    icon: <AssessmentIcon />,
    description: 'Demographic data for 5, 10, and 15 minute drive times'
  },
  nearby_schools: {
    label: 'Nearby Schools',
    icon: <GroupsIcon />,
    description: 'Nearby school relationships for 2019, 2023, and 2024'
  },
  school_metrics: {
    label: 'School Metrics',
    icon: <AssessmentIcon />,
    description: 'Computed school performance metrics'
  },
  school_projections: {
    label: 'Projections',
    icon: <InfoIcon />,
    description: 'Enrollment projections (public/updated)'
  },
  school_grades_offered: {
    label: 'Grades Offered',
    icon: <SchoolIcon />,
    description: 'Available grade levels at the school'
  },
  enrollment: {
    label: 'Enrollment Data',
    icon: <GroupsIcon />,
    description: 'Student enrollment totals and demographic breakdown'
  }
};

const StatusChip = ({ status, label, size = 'small' }) => {
  const getStatusProps = () => {
    if (status) {
      return {
        color: 'success',
        icon: <CheckCircleIcon />,
        variant: 'filled'
      };
    } else {
      return {
        color: 'error',
        icon: <ErrorIcon />,
        variant: 'outlined'
      };
    }
  };

  const props = getStatusProps();
  
  return (
    <Chip
      {...props}
      label={label || (status ? 'Complete' : 'Missing')}
      size={size}
      sx={{ minWidth: 80 }}
    />
  );
};

const DataPointDetail = ({ title, data, type }) => {
  if (type === 'esri_demographic_data') {
    return (
      <Stack spacing={1}>
        {[5, 10, 15].map(driveTime => {
          const dtData = data[`drive_time_${driveTime}`];
          return (
            <Box key={driveTime} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ minWidth: 60 }}>
                {driveTime} min:
              </Typography>
              <StatusChip status={dtData?.has_data} size="small" />
              {dtData?.timestamp && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(dtData.timestamp).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    );
  }

  if (type === 'nearby_schools') {
    return (
      <Stack spacing={1}>
        {[2019, 2023, 2024].map(year => {
          const yearData = data[`year_${year}`];
          const isExpected = yearData?.expected;
          const hasActualData = yearData?.has_data;
          const hasCount = yearData?.nearby_count > 0;
          
          return (
            <Box key={year} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ minWidth: 50 }}>
                {year}:
              </Typography>
              {!isExpected && !hasActualData ? (
                <Chip 
                  label="N/A" 
                  size="small" 
                  variant="outlined"
                  color="default"
                />
              ) : (
                <StatusChip status={hasActualData} size="small" />
              )}
              {hasCount && (
                <Typography variant="caption" color="text.secondary">
                  ({yearData.nearby_count})
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    );
  }

  if (type === 'enrollment') {
    return (
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2">Total:</Typography>
          <StatusChip status={data.has_data} size="small" />
          {data.total_enrollment > 0 && (
            <Typography variant="caption" color="text.secondary">
              ({data.total_enrollment})
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2">Demographics:</Typography>
          <StatusChip status={data.has_demographic_data} size="small" />
        </Box>
        {data.latest_year && (
          <Typography variant="caption" color="text.secondary">
            Latest: {data.latest_year}
          </Typography>
        )}
      </Stack>
    );
  }

  if (type === 'school_grades_offered') {
    return (
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusChip status={data.has_data} size="small" />
          {data.count > 0 && (
            <Typography variant="caption" color="text.secondary">
              ({data.count} grades)
            </Typography>
          )}
        </Box>
        {data.grades && data.grades.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {data.grades.slice(0, 5).map(grade => (
              <Chip key={grade} label={grade} size="small" variant="outlined" />
            ))}
            {data.grades.length > 5 && (
              <Typography variant="caption" color="text.secondary">
                +{data.grades.length - 5} more
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    );
  }

  if (type === 'school_metrics') {
    return (
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusChip status={data.has_data} size="small" />
          {data.metrics_count > 0 && (
            <Typography variant="caption" color="text.secondary">
              ({data.metrics_count} records)
            </Typography>
          )}
        </Box>
        {data.drive_times_available && data.drive_times_available.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Drive times: {data.drive_times_available.join(', ')} min
          </Typography>
        )}
        {data.latest_calculated && (
          <Typography variant="caption" color="text.secondary">
            Latest: {new Date(data.latest_calculated).toLocaleDateString()}
          </Typography>
        )}
        {data.note && (
          <Typography variant="caption" color="warning.main">
            Note: {data.note}
          </Typography>
        )}
      </Stack>
    );
  }

  if (type === 'school_projections') {
    return (
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusChip status={data.has_data} size="small" />
          {data.projection_count > 0 && (
            <Typography variant="caption" color="text.secondary">
              ({data.projection_count})
            </Typography>
          )}
        </Box>
        {data.projection_types && data.projection_types.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {data.projection_types.map(type => (
              <Chip 
                key={type} 
                label={type} 
                size="small" 
                variant="outlined"
                color={type === 'updated' ? 'primary' : 'default'}
              />
            ))}
          </Box>
        )}
        {data.latest_generated && (
          <Typography variant="caption" color="text.secondary">
            Latest: {new Date(data.latest_generated).toLocaleDateString()}
          </Typography>
        )}
      </Stack>
    );
  }

  // Default display for simple data points
  return (
    <Stack spacing={1}>
      <StatusChip status={data.has_data} size="small" />
      {data.value && (
        <Typography variant="caption" color="text.secondary">
          {data.value}
        </Typography>
      )}
      {data.latitude && data.longitude && (
        <Typography variant="caption" color="text.secondary">
          {data.latitude.toFixed(4)}, {data.longitude.toFixed(4)}
        </Typography>
      )}
    </Stack>
  );
};

const SchoolRow = ({ school, onRunService, onDeleteSchool, onReactivateSchool, isDeleting }) => {
  const [expanded, setExpanded] = useState(false);

  const getCompletenessColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'collapse' : 'expand'}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {school.display_name}
            </Typography>
            {school.ncessch && (
              <Typography variant="caption" color="text.secondary">
                NCES: {school.ncessch}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{school.state}</Typography>
        </TableCell>
        <TableCell>
          {(() => {
            const schoolType = detectSchoolType(school);
            const typeInfo = getSchoolTypeInfo(schoolType);
            return (
              <Tooltip title={typeInfo.description}>
                <Chip 
                  label={typeInfo.label}
                  size="small"
                  variant="outlined"
                  color={typeInfo.color}
                />
              </Tooltip>
            );
          })()}
        </TableCell>
        <TableCell>
          <Chip 
            label={school.team_name}
            size="small"
            variant="outlined"
            color="primary"
          />
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={school.completeness_score}
              color={getCompletenessColor(school.completeness_score)}
              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" sx={{ minWidth: 40 }}>
              {school.completeness_score}%
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ButtonGroup size="small" variant="outlined">
              <Tooltip title="Run Individual Services">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onRunService(school)}
                    color="primary"
                    disabled={isDeleting}
                  >
                    <BuildIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Process All Services">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onRunService(school, 'all')}
                    color="secondary"
                    disabled={isDeleting}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </ButtonGroup>
            
            {school.status === 'inactive' ? (
              <Tooltip title="Reactivate School">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onReactivateSchool(school)}
                    color="success"
                    disabled={isDeleting}
                  >
                    <RestoreIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <>
                {(() => {
                  const schoolType = detectSchoolType(school);
                  const canDelete = canDeleteSchoolType(schoolType);
                  
                  if (!canDelete) {
                    return (
                      <Tooltip title="Standard NCES schools cannot be deleted">
                        <span>
                          <IconButton size="small" disabled>
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    );
                  }
                  
                  return (
                    <Tooltip title={isDeleting ? "Deleting..." : "Delete School (Permanent)"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteSchool(school)}
                          color="error"
                          disabled={isDeleting}
                        >
                          {isDeleting ? <CircularProgress size={16} /> : <HardDeleteIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                })()}
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom>
                Data Validation Details
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Data Point</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Details</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(school.validation_status).map(([key, data]) => (
                      <TableRow key={key}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {DATA_POINT_CONFIGS[key]?.icon}
                            <Typography variant="body2">
                              {DATA_POINT_CONFIGS[key]?.label || key}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={data.has_data} />
                        </TableCell>
                        <TableCell>
                          <DataPointDetail
                            title=""
                            data={data}
                            type={key}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ServiceDialog = ({ open, onClose, school, onExecute }) => {
  const [selectedServices, setSelectedServices] = useState([]);

  const services = [
    { id: 'geocoding', name: 'Geocoding', description: 'Update coordinates' },
    { id: 'esri', name: 'ESRI Demographics', description: 'Update demographic data' },
    { id: 'nearby', name: 'Nearby Schools', description: 'Update nearby school relationships' },
    { id: 'enrollment', name: 'Enrollment', description: 'Update enrollment data' },
    { id: 'metrics', name: 'School Metrics', description: 'Recalculate school metrics' }
  ];

  const handleExecute = () => {
    onExecute(school, selectedServices);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Run Services for {school?.display_name}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the services you want to run for this school:
        </Typography>
        <Stack spacing={1}>
          {services.map(service => (
            <Paper key={service.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant={selectedServices.includes(service.id) ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    setSelectedServices(prev => 
                      prev.includes(service.id)
                        ? prev.filter(id => id !== service.id)
                        : [...prev, service.id]
                    );
                  }}
                  startIcon={<PlayArrowIcon />}
                >
                  {service.name}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {service.description}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleExecute}
          variant="contained"
          disabled={selectedServices.length === 0}
        >
          Run Selected Services
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SchoolsValidation = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // State management
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalSchools, setTotalSchools] = useState(0);
  const [summary, setSummary] = useState({});
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [schoolTypeFilter, setSchoolTypeFilter] = useState('all');
  const [completenessFilter, setCompletenessFilter] = useState([0, 100]);
  
  // Delete confirmation state
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    school: null,
    deleting: false // Track deletion in progress
  });
  
  // Dialog state
  const [serviceDialog, setServiceDialog] = useState({
    open: false,
    school: null
  });

  const fetchSchools = useCallback(async (pageNum = 0, pageSize = 25, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: pageNum,
        pageSize,
        ...filters
      };
      
      console.log('fetchSchools called with params:', params);
      const response = await adminService.getSchoolsValidationData(params);
      
      console.log('Response received:', response);
      
      // Validate response structure
      if (!response) {
        throw new Error('No response received from server');
      }
      
      if (!response.pagination) {
        console.error('Invalid response structure - missing pagination:', response);
        throw new Error('Invalid response structure from server');
      }
      
      setSchools(response.schools || []);
      setTotalSchools(response.pagination.total || 0);
      setSummary(response.summary || {});
      
    } catch (err) {
      console.error('Error fetching schools validation data:', err);
      setError(err.message || 'Failed to load schools data');
      enqueueSnackbar('Failed to load schools validation data', { variant: 'error' });
      // Set empty state on error
      setSchools([]);
      setTotalSchools(0);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const getFilters = useCallback(() => {
    const filters = {};
    
    if (searchTerm.trim()) {
      filters.search = searchTerm.trim();
    }
    if (stateFilter) {
      filters.state = stateFilter;
    }
    if (teamFilter.trim()) {
      filters.team = teamFilter.trim();
    }
    if (schoolTypeFilter && schoolTypeFilter !== 'all') {
      filters.schoolType = schoolTypeFilter;
    }
    if (completenessFilter[0] > 0) {
      filters.minCompleteness = completenessFilter[0];
    }
    
    console.log('getFilters result:', filters);
    return filters;
  }, [searchTerm, stateFilter, teamFilter, schoolTypeFilter, completenessFilter]);

  // Clear cache on component mount to ensure fresh data
  useEffect(() => {
    console.log('SchoolsValidation component mounted - clearing cache');
    clearAdminCache('schools-validation');
  }, []); // Run once on mount

  // Single useEffect for all data fetching
  useEffect(() => {
    console.log('Single useEffect triggered - page:', page, 'filters:', { searchTerm, stateFilter, teamFilter, schoolTypeFilter, completenessFilter });
    
    const timeoutId = setTimeout(() => {
      const filters = getFilters();
      console.log('Fetching with filters:', filters);
      fetchSchools(page, rowsPerPage, filters);
    }, searchTerm ? 500 : 0); // Debounce only for search term

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, stateFilter, teamFilter, schoolTypeFilter, completenessFilter, getFilters, fetchSchools]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRunService = (school, serviceType = 'individual') => {
    setServiceDialog({
      open: true,
      school
    });
  };

  const handleExecuteServices = async (school, services) => {
    // TODO: Implement service execution
    enqueueSnackbar(
      `Service execution for ${school.display_name} not yet implemented. Services: ${services.join(', ')}`,
      { variant: 'info' }
    );
  };

  const handleDeleteSchool = (school) => {
    setDeleteDialog({
      open: true,
      school,
      deleting: false
    });
  };

  const handleReactivateSchool = async (school) => {
    try {
      await adminService.reactivateSchool(school.uuid, enqueueSnackbar);
      enqueueSnackbar('School reactivated successfully', { variant: 'success' });
      await fetchSchools(page, rowsPerPage, getFilters());
    } catch (error) {
      // Error is handled by adminService
      console.error('Error reactivating school:', error);
    }
  };

  const confirmDeleteSchool = async () => {
    if (!deleteDialog.school) return;

    try {
      setDeleteDialog(prev => ({ ...prev, deleting: true }));
      
      await adminService.hardDeleteSchool(deleteDialog.school.uuid, false, enqueueSnackbar);
      enqueueSnackbar('School permanently deleted', { variant: 'success' });
      
      // Refresh the schools list
      await fetchSchools(page, rowsPerPage, getFilters());
      
      setDeleteDialog({ open: false, school: null, deleting: false });
    } catch (error) {
      // Error is handled by adminService
      console.error('Error deleting school:', error);
      setDeleteDialog(prev => ({ ...prev, deleting: false }));
    }
  };

  const handleSearch = useCallback(() => {
    // Search is now automatic via useEffect, but keep this for the Search button
    const filters = getFilters();
    setPage(0); // Reset to first page when searching
    fetchSchools(0, rowsPerPage, filters);
  }, [getFilters, fetchSchools, rowsPerPage]);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setStateFilter('');
    setTeamFilter('');
    setSchoolTypeFilter('all');
    setCompletenessFilter([0, 100]);
    setPage(0);
    // Auto-filtering via useEffect will handle the refresh
  }, []);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Clear the cache for schools validation data
      clearAdminCache('schools-validation');
      
      // Fetch fresh data with current filters
      const filters = getFilters();
      await fetchSchools(page, rowsPerPage, filters);
      
      enqueueSnackbar('Schools data refreshed successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error refreshing schools data:', err);
      enqueueSnackbar('Failed to refresh schools data', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  const handleExportData = () => {
    // TODO: Implement data export
    enqueueSnackbar('Data export not yet implemented', { variant: 'info' });
  };

  if (loading && schools.length === 0) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '60vh',
          gap: 3
        }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" color="text.secondary">
            Data Loading...
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Please wait while we fetch your schools validation data
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Paper elevation={0} sx={{ mb: 3, p: 3, bgcolor: 'background.default' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Schools Data Validation
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Validate and manage data completeness for schools assigned to teams.
        </Typography>
      </Paper>

      {/* Consolidated Search and Filters */}
      <Paper sx={{ mb: 3, p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Schools"
              placeholder="Search schools, NCES ID, or district..."
              value={searchTerm}
              disabled={loading}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select
                value={stateFilter}
                label="State"
                disabled={loading}
                onChange={(e) => setStateFilter(e.target.value)}
              >
                <MenuItem value="">All States</MenuItem>
                <MenuItem value="AL">Alabama</MenuItem>
                <MenuItem value="AK">Alaska</MenuItem>
                <MenuItem value="AZ">Arizona</MenuItem>
                <MenuItem value="AR">Arkansas</MenuItem>
                <MenuItem value="CA">California</MenuItem>
                <MenuItem value="CO">Colorado</MenuItem>
                <MenuItem value="CT">Connecticut</MenuItem>
                <MenuItem value="DE">Delaware</MenuItem>
                <MenuItem value="FL">Florida</MenuItem>
                <MenuItem value="GA">Georgia</MenuItem>
                <MenuItem value="HI">Hawaii</MenuItem>
                <MenuItem value="ID">Idaho</MenuItem>
                <MenuItem value="IL">Illinois</MenuItem>
                <MenuItem value="IN">Indiana</MenuItem>
                <MenuItem value="IA">Iowa</MenuItem>
                <MenuItem value="KS">Kansas</MenuItem>
                <MenuItem value="KY">Kentucky</MenuItem>
                <MenuItem value="LA">Louisiana</MenuItem>
                <MenuItem value="ME">Maine</MenuItem>
                <MenuItem value="MD">Maryland</MenuItem>
                <MenuItem value="MA">Massachusetts</MenuItem>
                <MenuItem value="MI">Michigan</MenuItem>
                <MenuItem value="MN">Minnesota</MenuItem>
                <MenuItem value="MS">Mississippi</MenuItem>
                <MenuItem value="MO">Missouri</MenuItem>
                <MenuItem value="MT">Montana</MenuItem>
                <MenuItem value="NE">Nebraska</MenuItem>
                <MenuItem value="NV">Nevada</MenuItem>
                <MenuItem value="NH">New Hampshire</MenuItem>
                <MenuItem value="NJ">New Jersey</MenuItem>
                <MenuItem value="NM">New Mexico</MenuItem>
                <MenuItem value="NY">New York</MenuItem>
                <MenuItem value="NC">North Carolina</MenuItem>
                <MenuItem value="ND">North Dakota</MenuItem>
                <MenuItem value="OH">Ohio</MenuItem>
                <MenuItem value="OK">Oklahoma</MenuItem>
                <MenuItem value="OR">Oregon</MenuItem>
                <MenuItem value="PA">Pennsylvania</MenuItem>
                <MenuItem value="RI">Rhode Island</MenuItem>
                <MenuItem value="SC">South Carolina</MenuItem>
                <MenuItem value="SD">South Dakota</MenuItem>
                <MenuItem value="TN">Tennessee</MenuItem>
                <MenuItem value="TX">Texas</MenuItem>
                <MenuItem value="UT">Utah</MenuItem>
                <MenuItem value="VT">Vermont</MenuItem>
                <MenuItem value="VA">Virginia</MenuItem>
                <MenuItem value="WA">Washington</MenuItem>
                <MenuItem value="WV">West Virginia</MenuItem>
                <MenuItem value="WI">Wisconsin</MenuItem>
                <MenuItem value="WY">Wyoming</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>School Type</InputLabel>
              <Select
                value={schoolTypeFilter}
                label="School Type"
                disabled={loading}
                onChange={(e) => {
                  console.log('School Type changed to:', e.target.value);
                  setSchoolTypeFilter(e.target.value);
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                {Object.entries(SCHOOL_TYPE_LABELS).map(([type, label]) => (
                  <MenuItem key={type} value={type}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Team"
              placeholder="Search by team name..."
              value={teamFilter}
              disabled={loading}
              onChange={(e) => setTeamFilter(e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ px: 1 }}>
              <Typography variant="body2" gutterBottom>
                Data Completeness: {completenessFilter[0]}% - {completenessFilter[1]}%
              </Typography>
              <Slider
                value={completenessFilter}
                disabled={loading}
                onChange={(e, newValue) => setCompletenessFilter(newValue)}
                valueLabelDisplay="auto"
                min={0}
                max={100}
                size="small"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <SchoolIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4">{summary.total_schools || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Schools
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <AssessmentIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4">{summary.avg_completeness || 0}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Completeness
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <WarningIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4">
                      {(schools || []).filter(s => s.completeness_score < 80).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Needs Attention
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4">
                      {(schools || []).filter(s => s.completeness_score >= 80).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Schools Complete
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Tooltip title="Clear cache and fetch fresh data from the database">
          <span>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={handleExportData}
        >
          Export Data
        </Button>
      </Box>

      {/* Schools Table */}
      <Paper>
        {loading && (
          <Box sx={{ 
            position: 'fixed', 
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'rgba(255, 255, 255, 0.95)', 
            borderRadius: 2,
            boxShadow: 3,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            zIndex: 1300, // Higher than MUI modal z-index
            minWidth: '300px'
          }}>
            <CircularProgress size={50} thickness={4} />
            <Typography variant="h6" color="text.secondary">
              Data Loading...
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {schoolTypeFilter !== 'all' && schoolTypeFilter 
                ? `Filtering ${SCHOOL_TYPE_LABELS[schoolTypeFilter] || schoolTypeFilter} schools...`
                : 'Updating schools data...'}
            </Typography>
          </Box>
        )}
        <TableContainer sx={{ position: 'relative' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>School</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Team</TableCell>
                <TableCell align="center">Data Completeness</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(schools || []).map((school) => (
                <SchoolRow
                  key={school.uuid}
                  school={school}
                  onRunService={handleRunService}
                  onDeleteSchool={handleDeleteSchool}
                  onReactivateSchool={handleReactivateSchool}
                  isDeleting={deleteDialog.deleting && deleteDialog.school?.uuid === school.uuid}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalSchools}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Service Dialog */}
      <ServiceDialog
        open={serviceDialog.open}
        onClose={() => setServiceDialog({ open: false, school: null })}
        school={serviceDialog.school}
        onExecute={handleExecuteServices}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={deleteDialog.deleting ? undefined : () => setDeleteDialog({ open: false, school: null, deleting: false })}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={deleteDialog.deleting}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HardDeleteIcon />
            Permanently Delete School
          </Box>
        </DialogTitle>
        <DialogContent>
          {deleteDialog.school && (
            <Box>
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
              >
                This will permanently delete all school data. This action cannot be undone.
              </Alert>
              
              <Typography variant="body1" gutterBottom>
                Are you sure you want to permanently delete this school?
              </Typography>
              
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  School Details:
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {deleteDialog.school.display_name || deleteDialog.school.name}
                </Typography>
                <Typography variant="body2">
                  <strong>State:</strong> {deleteDialog.school.state_abbr || deleteDialog.school.state || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>NCES ID:</strong> {deleteDialog.school.ncessch || 'None'}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> {getSchoolTypeInfo(detectSchoolType(deleteDialog.school)).label}
                </Typography>
              </Paper>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {getDeletionWarning(detectSchoolType(deleteDialog.school))}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, school: null, deleting: false })}
            disabled={deleteDialog.deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteSchool}
            color="error"
            variant="contained"
            disabled={deleteDialog.deleting}
            startIcon={deleteDialog.deleting ? <CircularProgress size={16} /> : <HardDeleteIcon />}
          >
            {deleteDialog.deleting ? 'Deleting...' : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SchoolsValidation; 